/**
 * Casos d'ús — "què ha passat / on fa mal / què faríem", lligat a reserves.
 * Vegeu `plans/reservation-context-and-use-cases.md` §F3. Compartit front/back.
 */

/**
 * Tema d'un cas. Sis valors **amples** a posta (ajust de F3): en Pere ho va dir
 * clar — «la classificació és important i alhora complicada per a nosaltres». Vuit
 * temes fins obligarien a decidir en el moment d'escriure, que és justament quan no
 * s'ha de decidir res. El que no hi encaixa va a `tags`, que és lliure.
 *
 * Les llistes definitives es tanquen a M3, amb els casos reals a la mà.
 */
export const USE_CASE_TOPICS = [
  'messaging',
  'billing',
  'calendar',
  'guest-data',
  'access',
  'other',
] as const;
export type UseCaseTopic = (typeof USE_CASE_TOPICS)[number];

/** Quina dada de la reserva fa mal en aquest cas. Sempre opcional. */
export const USE_CASE_DATA_KEYS = [
  'phone',
  'whatsapp',
  'persons',
  'beds',
  'residence',
  'company',
  'arrival-time',
  'invoice',
  'other',
] as const;
export type UseCaseDataKey = (typeof USE_CASE_DATA_KEYS)[number];

/**
 * Estat d'un cas. **Només avança**: `open → in-spec → done`.
 *
 * No hi ha cap camí de tornada ni cap esborrat dur. «Aquesta informació hauria de
 * persistir encara que es marqui com a resolta» [Pere, 09-SET-2026]: un cas resolt
 * segueix sent la raó per la qual el codi és com és.
 */
export const USE_CASE_STATUSES = ['open', 'in-spec', 'done'] as const;
export type UseCaseStatus = (typeof USE_CASE_STATUSES)[number];

/** `use-case` avui; `bug` queda reservat per si un dia el mateix calaix serveix. */
export const USE_CASE_KINDS = ['use-case', 'bug'] as const;
export type UseCaseKind = (typeof USE_CASE_KINDS)[number];

/**
 * Qui ha posat la classificació.
 *
 * `claude` = proposta de `scripts/dev/use-cases.mjs --classify`, que una persona
 * encara pot canviar des de la llista. Miranda no n'escriu cap: l'script és Claude
 * Code amb el token del `.env`, no una tool de l'agent.
 */
export const USE_CASE_TAGGERS = ['pere', 'carme', 'claude'] as const;
export type UseCaseTaggedBy = (typeof USE_CASE_TAGGERS)[number];

/** Referència a una pregunta oberta de `docs/research/converses-com-a-font-de-dades.md`. */
export type UseCaseQuestionRef = string; // P1…P17

/** Reserva lligada a un cas, tal com surt de l'API. */
export interface UseCaseReservationLink {
  /**
   * `null` si la reserva ja no hi és (purga de retenció, P10). El cas segueix
   * llegint-se: `beds24Id` es va desar en crear el vincle i no desapareix.
   */
  reservationId: string | null;
  beds24Id: string;
  guestName: string | null;
  checkIn: string | null;
  channel: string | null;
}

/** Un cas d'ús complet. */
export interface UseCase {
  id: string;
  whatHappened: string;
  pain: string;
  idea: string;

  topic: UseCaseTopic | null;
  dataKey: UseCaseDataKey | null;
  questionRef: UseCaseQuestionRef | null;
  tags: string[];
  taggedBy: UseCaseTaggedBy | null;
  taggedAt: string | null;

  kind: UseCaseKind;
  status: UseCaseStatus;

  specRef: string | null;
  resolvedAt: string | null;
  resolution: string | null;

  authorId: string;
  authorName: string | null;

  reservations: UseCaseReservationLink[];

  createdAt: string;
  updatedAt: string;
}

/** Cos de `POST /use-cases`. Només els tres textos i la reserva són obligatoris. */
export interface CreateUseCaseRequest {
  whatHappened: string;
  pain: string;
  idea: string;
  beds24Ids: string[];
  topic?: UseCaseTopic;
  dataKey?: UseCaseDataKey;
  questionRef?: string;
  tags?: string[];
  kind?: UseCaseKind;
  specRef?: string;
}

/**
 * Cos de `PATCH /use-cases/:id`.
 *
 * `status` només pot avançar i `done` exigeix `resolution` — el servidor ho
 * verifica, aquí només es declara la forma.
 */
export interface UpdateUseCaseRequest {
  whatHappened?: string;
  pain?: string;
  idea?: string;
  beds24Ids?: string[];
  topic?: UseCaseTopic | null;
  dataKey?: UseCaseDataKey | null;
  questionRef?: string | null;
  tags?: string[];
  kind?: UseCaseKind;
  status?: UseCaseStatus;
  specRef?: string | null;
  resolution?: string;
  /** Qui firma la classificació d'aquesta passada. Per a `--classify`. */
  taggedBy?: UseCaseTaggedBy;
}

/** Filtres de `GET /use-cases`. */
export interface FindUseCasesFilters {
  reservationId?: string;
  beds24Id?: string;
  topic?: UseCaseTopic;
  dataKey?: UseCaseDataKey;
  questionRef?: string;
  status?: UseCaseStatus;
  /** Casos encara sense `topic`, per a la passada de classificació. */
  untagged?: boolean;
  /** Cerca de text sobre els tres camps lliures. */
  q?: string;
  limit?: number;
  offset?: number;
}

export interface UseCasesResponse {
  data: UseCase[];
  total: number;
}

// ---------------------------------------------------------------------------
// Candidats: reserves per repassar a la sessió d'escriptura
// ---------------------------------------------------------------------------

/**
 * Estat del telèfon d'una reserva.
 *
 * Es calcula **en memòria** amb `junkPhoneReason` (la mateixa regla del precontacte)
 * i no és cap columna: per això no és un filtre de SQL sinó un filtre del servei.
 */
export type UseCasePhoneState = 'none' | 'ota-placeholder' | 'real';

/** `true | false | unknown` — `hasWhatsapp` és nullable a la fitxa. */
export type UseCaseWhatsappFilter = 'true' | 'false' | 'unknown';

/** Una fila de la pestanya "Candidats". */
export interface UseCaseCandidate {
  reservationId: string;
  beds24Id: string;
  guestName: string | null;
  channel: string | null;
  checkIn: string;
  checkOut: string;
  roomQty: number | null;
  phoneState: UseCasePhoneState;
  hasWhatsapp: boolean | null;
  hasClient: boolean;
  useCaseCount: number;
}

export interface UseCaseCandidatesFilters {
  /** Finestra de `checkIn`. Per defecte, els últims 90 dies; màxim 365. */
  checkInFrom?: string;
  checkInTo?: string;
  phoneState?: UseCasePhoneState;
  hasWhatsapp?: UseCaseWhatsappFilter;
  channel?: string;
  /** Només reserves de més d'una habitació. */
  groupsOnly?: boolean;
  hasClient?: boolean;
  /** `true` només amb casos, `false` només sense. */
  hasUseCases?: boolean;
  limit?: number;
  offset?: number;
}

export interface UseCaseCandidatesResponse {
  data: UseCaseCandidate[];
  total: number;
}
